import ActiveSelectionPanel from '@/app/ActiveSelectionPanel';
import { useSelectFlow } from '@/app/guide/guideEditBase';
import { FlowStatusBadges } from '@/app/guide/FlowStatusBadges';
import { useStartNewGuide } from '@/app/guide/useStartNewGuide';
import NodeLibrary from '@/app/NodeLibrary';
import { PreviewBadge, PublishedBadge } from '@/components/badges/Common';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { usePublishGuide } from '@/db/usePublishGuide';
import { usePullGuide } from '@/db/usePullGuide';
import { useRemovePublishedGuide } from '@/db/useRemovePublishedGuide';
import {
    currentFlowState,
    replaceFlowContentState,
    currentUserIdState,
    draftFlowsState,
    editFlowOpenState,
    editingFlowState,
    selectedDraftFlowState,
} from '@/state';
import {
    currentFlowStatusState,
    guideContextForParams,
    guideStagePendingState,
    liveGuideFlowState,
    previewGuideFlowIdState,
} from '@/state/guide.state';
import { useAsyncAtomValue, useUnwrappedAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { AppFlowDao } from '@rock/db-admin';
import type { AppFlowObject, AppFlowParams, GuideContext } from '@rock/types';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import {
    ChevronDown,
    CloudOff,
    DownloadCloud,
    Edit,
    FileDown,
    FileUp,
    MoreVertical,
    PlusCircle,
    Trash,
    UploadCloud,
} from 'lucide-react';
import { type FC, useContext, useState } from 'react';
import { toast } from 'sonner';
import DataKeysSummaryPanel from './DataKeysSummaryPanel';
import { FlowParamsContext } from './FlowInitializer';
import TypesUsagePanel from './TypesUsagePanel';

const log = logger('RightPanel');

// Fallback for the stage-pending read when there's no current flow (no context to query).
const NEVER_PENDING = atom(async () => false);
const appFlowDao = new AppFlowDao();

const RightPanel = () => {
    const params = useContext(FlowParamsContext);
    const selectedDraftFlowLoadable = useAtomValue(selectedDraftFlowState(params));
    const selectedDraftFlow =
        selectedDraftFlowLoadable.state === 'hasData' ? selectedDraftFlowLoadable.data : undefined;

    if (!selectedDraftFlow) {
        return <NewGuideButton params={params} />;
    }

    return (
        <div className="w-full flex flex-1 flex-col items-stretch gap-5">
            <div className="flex flex-col items-stretch gap-2 p-3">
                <div className="flex flex-row justify-between items-center relative gap-2">
                    {/*<SpaceSelector currentSpace={currentSpace} />*/}
                    {/*<BlockTypeSelector currentSpace={currentSpace} currentBlockTypeId={currentBlockTypeId} />*/}

                    <LiveGuideState params={params} />
                </div>
                <div className="flex flex-row justify-between items-start relative gap-1 min-w-0">
                    <FlowSelector params={params} selectedDraftFlow={selectedDraftFlow} />
                    <CurrentFlowDropdown params={params} selectedDraftFlow={selectedDraftFlow} />
                </div>
            </div>
            <Separator />
            <ActiveSelectionPanel
                params={params}
                noSelectionContent={() => (
                    <div className="flex flex-1 flex-col p-3 gap-5 overflow-scroll">
                        <NodeLibrary params={params} />
                        <Separator />
                        <TypesUsagePanel params={params} />
                        <Separator />
                        <DataKeysSummaryPanel params={params} />
                    </div>
                )}
            />
        </div>
    );
};

const NewGuideButton: FC<{ params: AppFlowParams }> = ({ params }) => {
    const { canUseDefault, startEmpty, startFromDefault } = useStartNewGuide(params);

    return (
        <div className="w-full flex flex-col items-center p-5 space-y-3">
            <h5>No guides have been created yet.</h5>
            <Button onClick={startEmpty}>Start New Guide (empty)</Button>
            {canUseDefault && <Button onClick={startFromDefault}>Start New Guide (from Default)</Button>}
        </div>
    );
};

const LiveGuideState: FC<{ params: AppFlowParams }> = ({ params }) => {
    const statusLoadable = useAtomValue(currentFlowStatusState(params));
    if (statusLoadable.state !== 'hasData') return null;
    return <FlowStatusBadges status={statusLoadable.data} />;
};

const FlowSelector: FC<{
    params: AppFlowParams;
    selectedDraftFlow?: AppFlowObject;
}> = ({ params, selectedDraftFlow }) => {
    const liveFlow = useUnwrappedAtomValue(liveGuideFlowState(params));
    const draftFlows = useUnwrappedAtomValue(draftFlowsState(params)) ?? [];
    const previewFlowId = useAsyncAtomValue(previewGuideFlowIdState(guideContextForParams(params)), undefined);
    const { startEmpty } = useStartNewGuide(params);
    const selectFlow = useSelectFlow(params);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className="flex flex-row items-start gap-1 min-w-0 rounded-md hover:bg-accent hover:text-accent-foreground cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    <h1 className="text-xl text-left">{selectedDraftFlow?.name}</h1>
                    <ChevronDown className="h-5 w-5 shrink-0 mt-1" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[var(--radix-popper-anchor-width)]">
                {draftFlows.map((flow) => (
                    <DropdownMenuItem
                        key={flow.id}
                        onClick={() => selectFlow(flow.id)}
                        className={`flex-row justify-between gap-3 ${flow.id === selectedDraftFlow?.id ? 'font-bold' : ''}`}
                    >
                        {flow.name || `Flow: ${flow.id}`}
                        <div className="flex flex-row gap-1">
                            {liveFlow?.id === flow.id && <PublishedBadge />}
                            {previewFlowId === flow.id && <PreviewBadge />}
                        </div>
                    </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={startEmpty}>
                    <PlusCircle className="mr-2 h-4 w-4" />
                    New Guide
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const CurrentFlowDropdown: FC<{ params: AppFlowParams; selectedDraftFlow?: AppFlowObject }> = ({
    params,
    selectedDraftFlow,
}) => {
    const userId = useAtomValue(currentUserIdState);
    const setEditingFlow = useSetAtom(editingFlowState);
    const setEditFlowOpen = useSetAtom(editFlowOpenState);
    const { busy: publishing, publishToPreview, publishToLive } = usePublishGuide(params);
    const [pulling, pullGuide] = usePullGuide(params);
    const [removing, removePublishedGuide] = useRemovePublishedGuide(params);
    const currentFlow = useUnwrappedAtomValue(currentFlowState(params));
    const replaceFlowContent = useSetAtom(replaceFlowContentState(params));

    // "In Preview" badge + Publish-to-Live gate: true when a preview is staged that isn't live yet.
    const stageContext: GuideContext | undefined = currentFlow
        ? currentFlow.equipmentTypeId
            ? { type: 'equipment', typeId: currentFlow.equipmentTypeId }
            : { type: 'space', spaceId: currentFlow.space }
        : undefined;
    const stagePending = useAsyncAtomValue(stageContext ? guideStagePendingState(stageContext) : NEVER_PENDING, false);

    const [exportOpen, setExportOpen] = useState(false);
    const exportJson = selectedDraftFlow
        ? JSON.stringify(
              {
                  spaceId: selectedDraftFlow.space,
                  nodes: selectedDraftFlow.nodes,
                  edges: selectedDraftFlow.edges,
                  nextId: selectedDraftFlow.nextId,
              },
              null,
              2,
          )
        : '';

    const [importOpen, setImportOpen] = useState(false);
    const [importText, setImportText] = useState('');
    const [importError, setImportError] = useState('');
    const [parsedImport, setParsedImport] = useState<any>();

    const validateImport = (text: string) => {
        setImportText(text);
        try {
            const parsed = JSON.parse(text);
            if (parsed.spaceId === undefined || !parsed.nodes || !parsed.edges || parsed.nextId === undefined) {
                setImportError('Missing required fields: spaceId, nodes, edges, nextId');
                setParsedImport(undefined);
                return;
            }
            if (parsed.spaceId !== currentFlow?.space) {
                setImportError('spaceId does not match current space');
                setParsedImport(undefined);
                return;
            }
            setImportError('');
            setParsedImport(parsed);
        } catch {
            setImportError('Invalid JSON');
            setParsedImport(undefined);
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const text = await file.text();
            validateImport(text);
        }
    };

    const handleImport = async () => {
        if (!parsedImport || !currentFlow) return;
        try {
            await replaceFlowContent({
                nodes: parsedImport.nodes,
                edges: parsedImport.edges,
                nextId: parsedImport.nextId,
            });
            toast.success('Imported Guide Successfully');
            setImportOpen(false);
        } catch (err) {
            log.error('Error importing guide', { err });
            toast.error('Error Importing Guide', {
                description: 'An unexpected error occurred.',
            });
        }
    };

    const handleCopyExport = async () => {
        try {
            await navigator.clipboard.writeText(exportJson);
            toast.success('Copied guide JSON');
        } catch {
            toast.error('Failed to copy');
        }
    };

    const handleDownloadExport = () => {
        const blob = new Blob([exportJson], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedDraftFlow?.id || 'guide'}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showPublishConfirm, setShowPublishConfirm] = useState(false);
    const [showPullConfirm, setShowPullConfirm] = useState(false);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

    const handleDeleteCurrentFlow = async () => {
        if (userId && selectedDraftFlow) {
            try {
                log.info(`Deleting flow: ${selectedDraftFlow}`);
                await appFlowDao.deleteDraftFlow(selectedDraftFlow.space, selectedDraftFlow.id);
            } catch (err) {
                log.error('Failed to delete guide', { err });
            }
        }
    };

    const handleEditCurrentFlow = () => {
        if (selectedDraftFlow) {
            setEditingFlow(selectedDraftFlow);
            setEditFlowOpen(true);
        }
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="p-0">
                        <MoreVertical className="h-5 w-5" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={handleEditCurrentFlow}>
                        <Edit className="mr-2 h-4 w-4" />
                        Edit Details
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowDeleteConfirm(true)}>
                        <Trash className="mr-2 h-4 w-4 text-red-600" />
                        Delete Guide
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setImportOpen(true)}>
                        <FileUp className="mr-2 h-4 w-4" />
                        Import Guide
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setExportOpen(true)}>
                        <FileDown className="mr-2 h-4 w-4" />
                        Export Guide
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={publishToPreview} disabled={publishing}>
                        <UploadCloud className="mr-2 h-4 w-4" />
                        Publish to Preview
                        {stagePending && <span className="ml-2 text-xs text-amber-700">● In Preview</span>}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={() => setShowPublishConfirm(true)}
                        disabled={publishing || !stagePending}
                    >
                        <UploadCloud className="mr-2 h-4 w-4" />
                        Publish to Live
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowPullConfirm(true)}>
                        <DownloadCloud className="mr-2 h-4 w-4" />
                        Pull Guide
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowRemoveConfirm(true)}>
                        <CloudOff className="mr-2 h-4 w-4" />
                        Remove Published Guide
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Delete: {selectedDraftFlow?.name}</AlertDialogTitle>
                        <AlertDialogDescription>
                            This cannot be undone. Are you sure you want to delete this guide?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={handleDeleteCurrentFlow}>
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showPublishConfirm} onOpenChange={setShowPublishConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Publish to Live</AlertDialogTitle>
                        <AlertDialogDescription>
                            This replaces the live guide in the App with the previewed version, for everyone. Are you
                            sure?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="cursor-pointer" disabled={publishing}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction className="cursor-pointer" disabled={publishing} onClick={publishToLive}>
                            Publish to Live
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showPullConfirm} onOpenChange={setShowPullConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Pull</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will replace your guide with what is currently published. It cannot be undone. Are you
                            sure?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="cursor-pointer" disabled={pulling}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction className="cursor-pointer" disabled={pulling} onClick={pullGuide}>
                            Pull Guide
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Remove</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will remove the published guide. Users will no longer be able to use the guided setup
                            for this {selectedDraftFlow?.equipmentTypeId ? 'equipment type' : 'space'}. Are you sure?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="cursor-pointer" disabled={removing}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            className="cursor-pointer"
                            disabled={removing}
                            onClick={removePublishedGuide}
                        >
                            Remove Guide
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog open={exportOpen} onOpenChange={setExportOpen}>
                <DialogContent className="max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Export Guide JSON</DialogTitle>
                    </DialogHeader>
                    <div className="max-h-[50vh] rounded border p-2 overflow-scroll">
                        <pre className="text-sm">{exportJson}</pre>
                    </div>
                    <DialogFooter className="flex flex-row justify-between sm:justify-between">
                        <Button variant="secondary" onClick={() => setExportOpen(false)}>
                            Close
                        </Button>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={handleCopyExport}>
                                Copy
                            </Button>
                            <Button onClick={handleDownloadExport}>Download</Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={importOpen}
                onOpenChange={(o) => {
                    setImportOpen(o);
                    if (!o) {
                        setImportText('');
                        setImportError('');
                        setParsedImport(undefined);
                    }
                }}
            >
                <DialogContent className="max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Import Guide JSON</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Input type="file" accept="application/json" onChange={handleFileUpload} />
                        <Textarea
                            value={importText}
                            onChange={(e) => validateImport(e.target.value)}
                            className="font-mono h-60"
                        />
                        {importError && <div className="text-red-500 text-sm">{importError}</div>}
                    </div>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setImportOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleImport} disabled={!parsedImport}>
                            Import
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default RightPanel;
